import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { errorMessage } from 'src/helpers/fns'
import { useAdminAccess, useApi, useDebouncedLoadOptions } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import { Button } from 'src/tailwind/components/Button'
import AsyncSelect from 'src/views/components/AsyncSelect'
import Loader from 'src/views/components/Loader'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import Swal from 'sweetalert2'
import { FormSection } from '../../programs/AdministratorProgramPage/FormSection'

interface CourseOption {
  readonly value: number
  readonly label: string
}

export default function AdministratorCoursePrerequisitesPage(): JSX.Element {
  const t = useTranslatable()
  return (
    <SuspenseWrapper title={t('course:prerequisites')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const headers = useAuthenticatedHeaders()
  const { courseId } = useParams()
  const [searchParams] = useSearchParams()
  const activeSemester = useActiveSemester()
  const semIdStr = searchParams.get('semId') ?? (activeSemester?.id != null ? String(activeSemester.id) : '')
  const parsedSemId = semIdStr !== '' ? parseInt(semIdStr, 10) : null

  if (courseId == null) {
    return <Loader className="m-auto mt-8 flex" />
  }

  return (
    <div className="mt-4 w-full space-y-6">
      <PrerequisitesSection courseId={courseId} headers={headers} parsedSemId={parsedSemId} />
      <CorequisitesSection courseId={courseId} headers={headers} parsedSemId={parsedSemId} />
    </div>
  )
}

function PrerequisitesSection({
  courseId,
  headers,
  parsedSemId,
}: {
  readonly courseId: string
  readonly headers: Api.getAdministratorCourseRequirementsParams['headers']
  readonly parsedSemId: number | null
}): JSX.Element {
  const t = useTranslatable()
  const canManageCourses = useAdminAccess().canManage('courses')
  const [pickerCourse, setPickerCourse] = React.useState<CourseOption | null>(null)
  const [pendingPrerequisites, setPendingPrerequisites] = React.useState<readonly CourseOption[]>([])
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const requirementsParams = React.useMemo(
    () => ({
      headers,
      args: { id: courseId },
      query: parsedSemId != null ? { semId: parsedSemId } : {},
    }),
    [courseId, headers, parsedSemId]
  )

  const {
    data: requirements,
    mutate,
    isLoading,
  } = useApi({
    endpoint: Api.getAdministratorCourseRequirements,
    params: requirementsParams,
    shouldFetch: true,
  })

  const handleDelete = React.useCallback(
    async (orGroup: number) => {
      const alert = await Swal.fire({
        title: t('common:are_you_sure'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('common:confirm'),
        cancelButtonText: t('common:cancel'),
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6C757D',
      })
      if (!alert.isConfirmed) return
      try {
        await Api.deleteAdministratorCourseRequirements({
          headers,
          args: { id: courseId, orGroup },
          query: parsedSemId != null ? { semId: parsedSemId } : {},
        })
        await mutate()
        toast.success(t(['წარმატებით წაიშალა', 'Successfully deleted']))
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
      }
    },
    [courseId, headers, mutate, parsedSemId, t]
  )

  const handleAdd = React.useCallback(async () => {
    if (pendingPrerequisites.length === 0) return
    setIsSubmitting(true)
    try {
      await Api.postAdministratorCourseRequirements({
        headers,
        args: { id: courseId },
        body: {
          requiredCouIds: pendingPrerequisites.map((c) => c.value),
          ...(parsedSemId != null && { semId: parsedSemId }),
        },
      })
      await mutate()
      setPendingPrerequisites([])
      setPickerCourse(null)
      toast.success(t('common:updated_successfully'))
    } catch (error) {
      toast.error(errorMessage(error as base.CommonError))
    } finally {
      setIsSubmitting(false)
    }
  }, [pendingPrerequisites, courseId, headers, parsedSemId, mutate, t])

  const currentCourseNumericId = React.useMemo(
    () => (courseId !== '' ? Number.parseInt(courseId, 10) : NaN),
    [courseId]
  )

  const handlePickerChange = React.useCallback(
    (option: CourseOption | null) => {
      if (option == null) {
        setPickerCourse(null)
        return
      }
      if (Number.isFinite(currentCourseNumericId) && option.value === currentCourseNumericId) {
        toast.error(
          t(['ამ კურსს ვერ დაამატებთ წინაპირობად', 'You cannot add this course as a prerequisite of itself.'])
        )
        setPickerCourse(null)
        return
      }
      setPendingPrerequisites((prev) => {
        if (prev.some((p) => p.value === option.value)) {
          return prev
        }
        return [...prev, option]
      })
      setPickerCourse(null)
    },
    [currentCourseNumericId, t]
  )

  const removePending = React.useCallback((courseIdToRemove: number) => {
    setPendingPrerequisites((prev) => prev.filter((c) => c.value !== courseIdToRemove))
  }, [])

  const groups = React.useMemo(() => requirements?.attributes ?? [], [requirements])

  const existingPrerequisiteCourseIds = React.useMemo(() => {
    const ids = new Set<number>()
    for (const group of groups) {
      for (const course of group.courses) {
        ids.add(course.id)
      }
    }
    return ids
  }, [groups])

  const loadCourseOptions = useDebouncedLoadOptions<CourseOption>(
    React.useCallback(
      async (q: string) => {
        const result = await Api.getAdministratorCourses({
          headers,
          query: {
            page: 1,
            perPage: 20,
            'filters[query]': q,
          },
        })
        return result.data
          .map((course) => ({
            value: Number.parseInt(course.id, 10),
            label: `${course.code} - ${course.name}`,
          }))
          .filter(
            (opt) =>
              (!Number.isFinite(currentCourseNumericId) || opt.value !== currentCourseNumericId) &&
              !pendingPrerequisites.some((p) => p.value === opt.value) &&
              !existingPrerequisiteCourseIds.has(opt.value)
          )
      },
      [headers, currentCourseNumericId, pendingPrerequisites, existingPrerequisiteCourseIds]
    ),
    { skipEmpty: true }
  )

  return (
    <div className="w-full min-w-0 rounded-card bg-card">
      <fieldset className="px-6 py-8">
        <FormSection title={t('course:prerequisites')}>
          {isLoading ? (
            <Loader className="m-auto flex py-8" />
          ) : groups.length === 0 ? (
            <p
              className="rounded-lg border border-dashed border-borderGrey bg-primaryWhite py-10 text-center text-caption text-captionColor"
              data-testid="prerequisitesEmpty"
            >
              {t(['წინაპირობები ჯერ არ არის დამატებული', 'No prerequisites have been added yet.'])}
            </p>
          ) : (
            <OrGroupList groups={groups} onDelete={handleDelete} />
          )}
        </FormSection>
      </fieldset>

      {canManageCourses && (
        <fieldset className="min-w-0 rounded-card bg-card px-6 py-8" disabled={isSubmitting}>
          <FormSection title={t(['ახალი წინაპირობის დამატება', 'Add a prerequisite'])}>
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="prerequisite-course-select"
                  className="mb-2 block text-sm font-medium text-primaryTextColor"
                >
                  {t('course:learning_course')}
                </label>
                <AsyncSelect<CourseOption>
                  inputId="prerequisite-course-select"
                  loadOptions={loadCourseOptions}
                  value={pickerCourse}
                  onChange={(option) => handlePickerChange(option)}
                  placeholder={t('course:learning_course')}
                  isClearable
                  cacheOptions
                  defaultOptions={false}
                  isDisabled={isSubmitting}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                  menuPosition="fixed"
                />
              </div>

              <p
                className="border-l-2 border-primaryBlue py-1 pl-3 text-caption leading-relaxed text-captionColor"
                role="note"
              >
                {t(['ან კავშირით დაკავშირებული წინაპირობები', 'Preconditions connected by or conjuctions'])}
              </p>

              {pendingPrerequisites.length > 0 && (
                <div className="space-y-2" data-testid="pendingPrerequisitesList">
                  <p className="text-sm font-medium text-primaryTextColor">
                    {t(['არჩეული წინაპირობები', 'Selected prerequisites'])}
                  </p>
                  <ul className="divide-y divide-borderGrey rounded-lg border border-borderGrey bg-primaryWhite">
                    {pendingPrerequisites.map((c) => (
                      <li key={c.value} className="flex items-center justify-between gap-3 px-3 py-2.5">
                        <span className="min-w-0 text-sm text-primaryTextColor">{c.label}</span>
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center justify-center rounded-md border border-borderGrey p-2 text-captionColor transition-colors hover:border-primaryRed hover:bg-red-50 hover:text-primaryRed"
                          onClick={() => removePending(c.value)}
                          aria-label={t('common:remove')}
                        >
                          <FontAwesomeIcon icon={ProRegularSvgIcons.faTimes} className="size-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-col gap-3 border-t border-borderGrey pt-5 sm:flex-row sm:justify-end">
                <Button
                  variant="blue"
                  type="button"
                  className="w-full sm:w-auto sm:min-w-[200px]"
                  onClick={() => void handleAdd()}
                  disabled={isSubmitting || pendingPrerequisites.length === 0}
                >
                  {t(['წინაპირობის დამატება', 'Prerequisites Add'])}
                </Button>
              </div>
            </div>
          </FormSection>
        </fieldset>
      )}
    </div>
  )
}

function CorequisitesSection({
  courseId,
  headers,
  parsedSemId,
}: {
  readonly courseId: string
  readonly headers: Api.getAdministratorCourseCorequisitesParams['headers']
  readonly parsedSemId: number | null
}): JSX.Element {
  const t = useTranslatable()
  const canManageCourses = useAdminAccess().canManage('courses')
  const [pickerCourse, setPickerCourse] = React.useState<CourseOption | null>(null)
  const [pendingCorequisites, setPendingCorequisites] = React.useState<readonly CourseOption[]>([])
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const corequisitesParams = React.useMemo(
    () => ({
      headers,
      args: { id: courseId },
      query: parsedSemId != null ? { semId: parsedSemId } : {},
    }),
    [courseId, headers, parsedSemId]
  )

  const {
    data: corequisites,
    mutate,
    isLoading,
  } = useApi({
    endpoint: Api.getAdministratorCourseCorequisites,
    params: corequisitesParams,
    shouldFetch: true,
  })

  const handleDelete = React.useCallback(
    async (orGroup: number) => {
      const alert = await Swal.fire({
        title: t('common:are_you_sure'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('common:confirm'),
        cancelButtonText: t('common:cancel'),
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6C757D',
      })
      if (!alert.isConfirmed) return
      try {
        await Api.deleteAdministratorCourseCorequisites({
          headers,
          args: { id: courseId, orGroup },
          query: parsedSemId != null ? { semId: parsedSemId } : {},
        })
        await mutate()
        toast.success(t(['წარმატებით წაიშალა', 'Successfully deleted']))
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
      }
    },
    [courseId, headers, mutate, parsedSemId, t]
  )

  const handleAdd = React.useCallback(async () => {
    if (pendingCorequisites.length === 0) return
    setIsSubmitting(true)
    try {
      await Api.postAdministratorCourseCorequisites({
        headers,
        args: { id: courseId },
        body: {
          requiredCouIds: pendingCorequisites.map((c) => c.value),
          ...(parsedSemId != null && { semId: parsedSemId }),
        },
      })
      await mutate()
      setPendingCorequisites([])
      setPickerCourse(null)
      toast.success(t('common:updated_successfully'))
    } catch (error) {
      toast.error(errorMessage(error as base.CommonError))
    } finally {
      setIsSubmitting(false)
    }
  }, [pendingCorequisites, courseId, headers, parsedSemId, mutate, t])

  const currentCourseNumericId = React.useMemo(
    () => (courseId !== '' ? Number.parseInt(courseId, 10) : NaN),
    [courseId]
  )

  const handlePickerChange = React.useCallback(
    (option: CourseOption | null) => {
      if (option == null) {
        setPickerCourse(null)
        return
      }
      if (Number.isFinite(currentCourseNumericId) && option.value === currentCourseNumericId) {
        toast.error(t(['ამ კურსს ვერ დაამატებთ თანაპირობად', 'You cannot add this course as a corequisite of itself.']))
        setPickerCourse(null)
        return
      }
      setPendingCorequisites((prev) => {
        if (prev.some((p) => p.value === option.value)) {
          return prev
        }
        return [...prev, option]
      })
      setPickerCourse(null)
    },
    [currentCourseNumericId, t]
  )

  const removePending = React.useCallback((courseIdToRemove: number) => {
    setPendingCorequisites((prev) => prev.filter((c) => c.value !== courseIdToRemove))
  }, [])

  const groups = React.useMemo(() => corequisites?.attributes ?? [], [corequisites])

  const existingCorequisiteCourseIds = React.useMemo(() => {
    const ids = new Set<number>()
    for (const group of groups) {
      for (const course of group.courses) {
        ids.add(course.id)
      }
    }
    return ids
  }, [groups])

  const loadCourseOptions = useDebouncedLoadOptions<CourseOption>(
    React.useCallback(
      async (q: string) => {
        const result = await Api.getAdministratorCourses({
          headers,
          query: {
            page: 1,
            perPage: 20,
            'filters[query]': q,
          },
        })
        return result.data
          .map((course) => ({
            value: Number.parseInt(course.id, 10),
            label: `${course.code} - ${course.name}`,
          }))
          .filter(
            (opt) =>
              (!Number.isFinite(currentCourseNumericId) || opt.value !== currentCourseNumericId) &&
              !pendingCorequisites.some((p) => p.value === opt.value) &&
              !existingCorequisiteCourseIds.has(opt.value)
          )
      },
      [headers, currentCourseNumericId, pendingCorequisites, existingCorequisiteCourseIds]
    ),
    { skipEmpty: true }
  )

  return (
    <div className="w-full min-w-0 rounded-card bg-card">
      <fieldset className="px-6 py-8">
        <FormSection title={t(['თანაპირობები', 'Corequisites'])}>
          {isLoading ? (
            <Loader className="m-auto flex py-8" />
          ) : groups.length === 0 ? (
            <p
              className="rounded-lg border border-dashed border-borderGrey bg-primaryWhite py-10 text-center text-caption text-captionColor"
              data-testid="corequisitesEmpty"
            >
              {t(['თანაპირობები ჯერ არ არის დამატებული', 'No corequisites have been added yet.'])}
            </p>
          ) : (
            <OrGroupList groups={groups} onDelete={handleDelete} />
          )}
        </FormSection>
      </fieldset>

      {canManageCourses && (
        <fieldset className="min-w-0 rounded-card bg-card px-6 py-8" disabled={isSubmitting}>
          <FormSection title={t(['ახალი თანაპირობების დამატება', 'Add a corequisite'])}>
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="corequisite-course-select"
                  className="mb-2 block text-sm font-medium text-primaryTextColor"
                >
                  {t('course:learning_course')}
                </label>
                <AsyncSelect<CourseOption>
                  inputId="corequisite-course-select"
                  loadOptions={loadCourseOptions}
                  value={pickerCourse}
                  onChange={(option) => handlePickerChange(option)}
                  placeholder={t('course:learning_course')}
                  isClearable
                  cacheOptions
                  defaultOptions={false}
                  isDisabled={isSubmitting}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                  menuPosition="fixed"
                />
              </div>

              <p
                className="border-l-2 border-primaryBlue py-1 pl-3 text-caption leading-relaxed text-captionColor"
                role="note"
              >
                {t(['ან კავშირით დაკავშირებული თანაპირობები', 'Corequisites connected by or conjunctions'])}
              </p>

              {pendingCorequisites.length > 0 && (
                <div className="space-y-2" data-testid="pendingCorequisitesList">
                  <p className="text-sm font-medium text-primaryTextColor">
                    {t(['არჩეული თანაპირობები', 'Selected corequisites'])}
                  </p>
                  <ul className="divide-y divide-borderGrey rounded-lg border border-borderGrey bg-primaryWhite">
                    {pendingCorequisites.map((c) => (
                      <li key={c.value} className="flex items-center justify-between gap-3 px-3 py-2.5">
                        <span className="min-w-0 text-sm text-primaryTextColor">{c.label}</span>
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center justify-center rounded-md border border-borderGrey p-2 text-captionColor transition-colors hover:border-primaryRed hover:bg-red-50 hover:text-primaryRed"
                          onClick={() => removePending(c.value)}
                          aria-label={t('common:remove')}
                        >
                          <FontAwesomeIcon icon={ProRegularSvgIcons.faTimes} className="size-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-col gap-3 border-t border-borderGrey pt-5 sm:flex-row sm:justify-end">
                <Button
                  variant="blue"
                  type="button"
                  className="w-full sm:w-auto sm:min-w-[200px]"
                  onClick={() => void handleAdd()}
                  disabled={isSubmitting || pendingCorequisites.length === 0}
                >
                  {t(['თანაპირობის დამატება', 'Corequisites Add'])}
                </Button>
              </div>
            </div>
          </FormSection>
        </fieldset>
      )}
    </div>
  )
}

function OrGroupList({
  groups,
  onDelete,
}: {
  readonly groups: readonly Api.AdministratorCourseRequirementOrGroup[]
  readonly onDelete: (orGroup: number) => void
}): JSX.Element {
  const t = useTranslatable()
  const canManageCourses = useAdminAccess().canManage('courses')

  return (
    <ul className="w-full space-y-3">
      {groups.map((group) => (
        <li
          key={group.orGroup}
          className="flex w-full cursor-pointer flex-col gap-3 rounded-lg border border-borderGrey bg-primaryWhite p-4 sm:flex-row sm:items-center sm:justify-between"
          onClick={() => onDelete(group.orGroup)}
        >
          <div className="flex w-full min-w-0 flex-1 flex-wrap items-center gap-2">
            {group.courses.map((course: Api.AdministratorCourseRequirementCourseRef, index: number) => (
              <React.Fragment key={course.id}>
                {index > 0 ? (
                  <span
                    className="shrink-0 rounded bg-primaryBlue/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-primaryBlue"
                    aria-hidden
                  >
                    {t(['ან', 'OR'])}
                  </span>
                ) : null}
                <span className="min-w-0 text-primaryTextColor">
                  <span className="font-mono text-sm font-semibold text-captionColor">{course.code}</span>
                  <span className="mx-1.5 text-captionColor">·</span>
                  <span className="text-sm font-medium">{course.name}</span>
                </span>
              </React.Fragment>
            ))}
          </div>
          {canManageCourses && (
            <button
              type="button"
              className="inline-flex shrink-0 items-center justify-center self-end rounded-md border border-borderGrey p-2.5 text-captionColor transition-colors hover:border-primaryRed hover:bg-red-50 hover:text-primaryRed sm:self-center"
              onClick={() => onDelete(group.orGroup)}
              aria-label={t('common:delete')}
            >
              <FontAwesomeIcon icon={ProRegularSvgIcons.faTrashCan} className="size-4" />
            </button>
          )}
        </li>
      ))}
    </ul>
  )
}
